import { useSelector } from "react-redux";
import {
  selectPermissions,
  selectPermissionsLoaded,
} from "../../store/slices/authSlice";
import { isAdminRole, normalizeRole } from "../../utils/roles";
import AccessDenied from "./AccessDenied";

/**
 * Normalize a role to its access-level category: "admin" | "staff" |
 * "member" | "customer" | "kiosk". Custom staff roles (manager,
 * junior_staff, senior_coach, ...) collapse to "staff". Role
 * classification itself lives in utils/roles (single source of truth).
 */
const getNormalizedRoleCategory = (role: string | null): string | null => {
  if (!role) return null;

  // Admin-equivalent roles (handles "Tenant Owner"/"tenant owner"/"tenant_owner").
  if (isAdminRole(role)) return "admin";

  const normalized = normalizeRole(role);
  if (normalized === "member") return "member";
  // Customers are member-portal accounts (utils/roles isMemberRole): a
  // signed-in customer must reach the member pages — their first enrollment
  // auto-promotes them to member anyway. Server-side authorization is
  // per-membership, so this grants no data another member couldn't see.
  if (normalized === "customer") return "member";
  if (normalized === "kiosk") return "kiosk";
  // Any other concrete role (staff, manager, junior_staff, ...) is "staff".
  return "staff";
};

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermission?: string; // Optional: the specific permission required to access this route
  allowedRoles?: string[]; // Optional: restrict access to one or more roles
}
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermission,
  allowedRoles,
}) => {
  // ✅ Get current user permissions from Redux store
  const permissions = useSelector(selectPermissions);
  const permissionsLoaded = useSelector(selectPermissionsLoaded);

  // ✅ Get current user role from localStorage
  const role: string | null = localStorage.getItem("userRole");

  // Normalize role to its access level category for allowedRoles check
  // e.g., "manager" → "staff", "tenant owner" → "admin"
  const normalizedRole = getNormalizedRoleCategory(role);

  // ✅ Admin and tenant owner users have full access - bypass permission checks
  // This ensures central auth users (tenant owners) and admins always have access
  const isAdminUser = normalizedRole === "admin";
  if (isAdminUser) {
    return <>{children}</>;
  }

  // ✅ Role gate — ALWAYS enforced. The role comes from localStorage and is
  //    available immediately, so it does not depend on permissions having
  //    loaded. (2026-06-10 audit C7: the previous guard skipped this check
  //    whenever `permissions` was an empty array — which is its initial state
  //    before the permissions fetch resolves, AND the state after a failed
  //    fetch — letting a member render staff pages. Enforcing the role gate
  //    unconditionally closes that bypass.)
  const roleAllowed = !allowedRoles || allowedRoles.includes(normalizedRole || "");
  if (!roleAllowed) {
    return <AccessDenied />; // ⛔ Block access
  }

  // ✅ Permission gate — only when a specific permission is required. We must
  //    distinguish "not loaded yet" from "loaded but the user lacks it":
  //    - not loaded → wait (do NOT fail open, do NOT wrongly deny)
  //    - loaded + missing → AccessDenied
  if (requiredPermission) {
    if (!permissionsLoaded) {
      return (
        <div
          role="status"
          aria-live="polite"
          className="flex items-center justify-center min-h-[40vh]"
        >
          <span className="sr-only">Checking permissions…</span>
          <div className="h-8 w-8 rounded-full border-2 border-gray-300 border-t-gray-600 dark:border-gray-600 dark:border-t-gray-300 animate-spin" />
        </div>
      );
    }
    const hasPermission = permissions?.some(
      (perm: any) => perm.name === requiredPermission
    );
    if (!hasPermission) {
      return <AccessDenied />; // ⛔ Block access
    }
  }

  // ✅ Otherwise, render the protected page
  return <>{children}</>;
};
export default ProtectedRoute;
